import { useCallback, useState } from "react"
import { Link } from "@tanstack/react-router"
import {
	IconArrowUpOutline18 as MakePrimary,
	IconPlusOutline18 as Plus,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"

import type { BrandfetchData } from "@productlane/api/src/browser"
import { generateId } from "@productlane/lib/src/browser"
import {
	AvatarStack,
	Button,
	IconButton,
	Popover,
	useDarkMode,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useZero } from "@/lib/zero"
import { CompanyLogo, pickCompanyLogoFormat } from "../customers/company-logo"
import { CustomersSelect } from "../customers/customers-select"

export interface LinkedCompany {
	id: string
	name: string
	linearLogoUrl?: string | null
	companyMeta?: { brandfetchData?: unknown } | null
}

interface Props {
	contactId: string
	primaryCompanyId: string | null
	companies: LinkedCompany[]
	onPromote: (companyId: string) => void
}

const brandfetchOf = (company: LinkedCompany) =>
	(company.companyMeta?.brandfetchData ?? null) as BrandfetchData | null

// One shape for every logo. A rounded square beside circles reads as two
// different kinds of thing.
const Logo = ({
	company,
	size,
}: {
	company: LinkedCompany
	size: "xxs+" | "xs" | "s"
}) => (
	<CompanyLogo
		brandfetchData={brandfetchOf(company)}
		linearLogoUrl={company.linearLogoUrl ?? undefined}
		fallback={company.name}
		size={size}
		roundness="md"
		className="flex-none"
	/>
)

/**
 * One person often works with several customers. The trigger collapses them to a
 * logo stack so the header keeps its width whatever the count.
 */
export const ContactCompanies = ({
	contactId,
	primaryCompanyId,
	companies,
	onPromote,
}: Props) => {
	const zero = useZero()
	const { isDarkMode } = useDarkMode()
	const [isOpen, setIsOpen] = useState(false)

	const link = useCallback(
		async (companyId: string) => {
			const result = await zero.mutate(
				mutators.contacts.linkCompany({
					id: generateId(),
					// Ids are generated by the caller so the optimistic and server runs
					// of the mutator insert the same rows.
					primaryLinkId: generateId(),
					contactId,
					companyId,
				}),
			).client
			if (result.type !== "success") {
				toast.error({ title: "Couldn't add that customer" })
			}
		},
		[contactId, zero],
	)

	const unlink = useCallback(
		async (companyId: string) => {
			const result = await zero.mutate(
				mutators.contacts.unlinkCompany({ contactId, companyId }),
			).client
			if (result.type !== "success") {
				toast.error({ title: "Couldn't remove that customer" })
			}
		},
		[contactId, zero],
	)

	const addCustomer = (
		<CustomersSelect
			companyId={null}
			align="start"
			onChange={(company) => {
				if (company?.id && !companies.some((c) => c.id === company.id)) {
					void link(company.id)
				}
			}}
			trigger={
				<div className="flex w-full cursor-default select-none items-center gap-2 rounded-md px-1.5 py-1 text-xs text-secondary hover:bg-gray-100 dark:hover:bg-gray-800">
					<Plus className="h-3.5 w-3.5 flex-none" />
					<span>Add customer</span>
				</div>
			}
		/>
	)

	if (companies.length === 0) {
		return <RoleCheck not={["VIEWER"]}>{addCustomer}</RoleCheck>
	}

	// The primary leads, so the collapsed label always names the company every
	// single-customer surface is showing.
	const ordered = [
		...companies.filter((c) => c.id === primaryCompanyId),
		...companies.filter((c) => c.id !== primaryCompanyId),
	]
	const [lead, ...rest] = ordered
	const stackAvatars = ordered.map((company) => ({
		name: company.name,
		image:
			company.linearLogoUrl ??
			pickCompanyLogoFormat(brandfetchOf(company), isDarkMode)?.src,
	}))

	return (
		<Popover
			open={isOpen}
			onOpenChange={setIsOpen}
			align="start"
			className="w-64 p-1"
			content={
				<div className="flex max-h-80 flex-col gap-0.5 overflow-y-auto pb-1">
					{ordered.map((company) => {
						const isPrimary = company.id === primaryCompanyId
						return (
							<div
								key={company.id}
								className="group flex cursor-default select-none items-center gap-2 rounded-md px-1.5 py-1 hover:bg-gray-100 dark:hover:bg-gray-800"
							>
								{/* Logo and name are one target so the whole label opens the
								    company, not just the two words. */}
								<Link
									to="/customers/$id"
									params={{ id: company.id }}
									className="group/link flex min-w-0 flex-1 cursor-default items-center gap-2 text-secondary hover:text-primary"
								>
									<Logo company={company} size="xs" />
									<span className="truncate text-xs group-hover/link:underline">
										{company.name}
									</span>
								</Link>
								{isPrimary && (
									<span className="flex-none text-[0.7rem] text-tertiary">
										Primary
									</span>
								)}
								<RoleCheck not={["VIEWER"]}>
									<div className="flex flex-none items-center gap-0.5">
										{!isPrimary && (
											<IconButton
												variant="ghost-brightness"
												size="sm"
												Icon={MakePrimary}
												className="text-secondary opacity-0 group-hover:opacity-100"
												onClick={() => onPromote(company.id)}
											/>
										)}
										<IconButton
											variant="ghost-brightness"
											size="sm"
											Icon={X}
											className="text-secondary opacity-0 group-hover:opacity-100"
											onClick={() => void unlink(company.id)}
										/>
									</div>
								</RoleCheck>
							</div>
						)
					})}
					<RoleCheck not={["VIEWER"]}>
						<div className="mt-0.5 border-t border-gray-200 pt-1 dark:border-gray-700/80">
							{addCustomer}
						</div>
					</RoleCheck>
				</div>
			}
		>
			<Button
				variant="ghost"
				size="sm"
				className="cursor-default select-none px-1.5 data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800"
				wrapperClassName="gap-2"
			>
				<AvatarStack
					avatars={stackAvatars}
					size="xxs"
					slice={3}
					roundness="md"
					ringClassName="ring-white dark:ring-gray-900"
				/>
				<span className="truncate text-xs">{lead?.name}</span>
				{rest.length > 0 && (
					<span className="flex-none text-xs text-tertiary">
						+{rest.length}
					</span>
				)}
			</Button>
		</Popover>
	)
}
